import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Stack,
  Box,
  Typography,
  Toolbar,
  Divider,
  Button,
  Link,
} from '@mui/material';

import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router';
import type {
  GridPaginationModel,
  GridRowSelectionModel,
  GridColDef,
  GridRowId,
} from '@mui/x-data-grid';

import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import { useApi } from '@/providers/ApiContext';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { deletePlot, getPlots } from '@/actions/plot';
import PlotDeleteDialog from '../PlotDeleteDialog';
import type { Plot, EntityId } from '@/types';

const PAGE_SIZE = 20;

interface PlotsProps {
  type?: string;
}

function Plots({ type }: PlotsProps): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const [plots, setPlots] = useState<Plot[] | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [api_error, setApiError] = useState<string | null>(null);

  const base_path = '/cartographer/plot';

  const fetchPlots = useCallback(
    async (page: number, startKey: string | null = null) => {
      if (!api) return;
      setLoading(true);
      try {
        const response = await getPlots(
          api,
          type || '',
          PAGE_SIZE,
          startKey || undefined,
        );
        setPlots(response.results);
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: response.last_key!,
          }));
        }
      } catch (error) {
        console.error('Error fetching plots:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to fetch plots. This could be due to network issues or server problems.';
        setApiError(errorMessage);
        setPlots([]);
      } finally {
        setLoading(false);
      }
    },
    [api, type],
  );

  useEffect(() => {
    if (user?.entity_id && !plots) {
      setCurrentPage(0);
      setCursors({});
      fetchPlots(0);
    }
  }, [user?.entity_id, plots, fetchPlots]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.page > currentPage && hasNextPage) {
        setCurrentPage(model.page);
        const nextCursor = cursors[model.page];
        if (nextCursor) {
          fetchPlots(model.page, nextCursor);
        }
      } else if (model.page < currentPage) {
        setCurrentPage(model.page);
        if (model.page === 0) {
          fetchPlots(0);
        } else {
          const prevCursor = cursors[model.page];
          if (prevCursor) {
            fetchPlots(model.page, prevCursor);
          }
        }
      }
    },
    [currentPage, hasNextPage, cursors, fetchPlots],
  );

  const handleDeletePlot = async (plot_id: EntityId) => {
    if (!api) return;
    try {
      await deletePlot(api, plot_id);
      setCurrentPage(0);
      setCursors({});
      await fetchPlots(0);
    } catch (error) {
      console.error('Error deleting plot:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to delete the plot. This could be due to network issues, server problems, or insufficient permissions.';
      setApiError(errorMessage);
    }
  };

  const handleRowSelection = useCallback((value: GridRowSelectionModel) => {
    setRowSelectionModel(value);
  }, []);

  const renderName = useCallback(
    (params: { id: string | number; row: Plot }) => {
      return (
        <Link component={RouterLink} to={`${base_path}/${String(params.id)}`}>
          {params.row.name}
        </Link>
      );
    },
    [base_path],
  );

  const columns = useMemo<GridColDef<Plot>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
      },
      {
        field: 'created_at',
        headerName: 'Started',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: (value) =>
          value && new Date(String(value).replace(' ', 'T') + 'Z'),
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        type: 'dateTime',
        flex: 0.75,
        valueGetter: (value) =>
          value && new Date(String(value).replace(' ', 'T') + 'Z'),
      },
    ],
    [renderName],
  );

  if (!plots) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          Document Creator
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() => {
              navigate(`${base_path}/edit`);
            }}
            variant='contained'
            startIcon={<AddCircleOutlineOutlinedIcon />}
          >
            New Document Plot
          </Button>
          <Button
            onClick={() => {
              const selectedIds = Array.from(rowSelectionModel.ids);
              navigate({
                pathname: `${base_path}/edit`,
                search: `?plot_id=${selectedIds[0]}`,
              });
            }}
            disabled={rowSelectionModel.ids.size !== 1}
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            disabled={rowSelectionModel.ids.size !== 1}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}
      >
        <DataGrid
              checkboxSelection
              disableRowSelectionOnClick
              columnHeaderHeight={35}
              sx={(theme) => ({
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.grey[100],
                },
              })}
              getRowId={(row) => row.entity_id}
              columns={columns}
              rows={plots || []}
              onRowSelectionModelChange={handleRowSelection}
              rowSelectionModel={rowSelectionModel}
              showToolbar
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  sx: {
                    button: {
                      color: 'black',
                    },
                    svg: {
                      color: 'black',
                    },
                  },
                  showQuickFilter: true,
                },
              }}
              paginationMode='server'
              rowCount={
                hasNextPage ? -1 : currentPage * PAGE_SIZE + plots.length
              }
              pageSizeOptions={[PAGE_SIZE]}
              paginationModel={{
                pageSize: PAGE_SIZE,
                page: currentPage,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: PAGE_SIZE,
                    page: 0,
                  },
                },
              }}
            />
      </Box>
      <PlotDeleteDialog
        key={`delete_${plots?.find((plot) => {
          const selectedIds = Array.from(rowSelectionModel.ids);
          return plot.entity_id === selectedIds[0];
        })?.updated_at || ''}`}
        plot={plots?.find((plot) => {
          const selectedIds = Array.from(rowSelectionModel.ids);
          return plot.entity_id === selectedIds[0];
        })}
        open={deleteOpen}
        deletePlot={handleDeletePlot}
        handleClose={() => setDeleteOpen(false)}
      />

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default Plots;
